import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";

import {
  type GDMTMedicationResult,
  type GDMTResult,
  MedicationGroup,
} from "../../../api";
import { SupportedMedications } from "../../../catalogs/supported-medications";

/**
 * The GDMT pillars as a fixed grid, after Fiuzat et al. (J Am Coll Cardiol
 * 2022;79(5):504-510): beta-blocker and MRA on the top row, RAAS inhibition and
 * SGLT2 inhibitor beneath. Each pillar keeps its place whether or not the
 * patient is on it, so a gap reads as a gap.
 */
export interface GdmtSlot {
  key: GdmtSlotKey;
  label: MessageDescriptor;
  /** Points achievable in this slot for the patient's HF type. */
  maxPoints: number;
  points: number;
  /** Every drug the patient takes in this slot's classes, scoring ones first. */
  drugs: GDMTMedicationResult[];
  state: GdmtSlotState;
}

export type GdmtSlotKey = "beta" | "mra" | "raas" | "sglt2";

/** Red / orange / green, per the tracker spec. */
export type GdmtSlotState = "missing" | "partial" | "at-target";

/**
 * The slots applicable to this result's HF type, in reading order.
 *
 * Only the layout is fixed here — which classes score, what they're worth and
 * which HF types they apply to all come from the catalog, so the grid follows
 * the backend rather than restating it. HFpEF and HFmrEF score SGLT2 inhibitors
 * alone, which collapses the grid to a single slot.
 */
export function gdmtSlots(
  result: GDMTResult,
  catalog: SupportedMedications,
): GdmtSlot[] {
  return SLOTS.map(({ key, label, classes }) => {
    // A class counts only when the catalog says it scores for this HF type.
    const applicable = classes(catalog).filter((group) =>
      result.hf_type == null
        ? false
        : (catalog.gdmtInfo(group)?.hf_types ?? []).includes(result.hf_type),
    );
    if (applicable.length === 0) return null;

    // The ceiling is the best any class in the slot can score, so a patient on
    // an ACE inhibitor (2) still reads as short of the ARNI-led maximum (3) —
    // matching the total the score ring is drawn against.
    const maxPoints = Math.max(
      ...applicable.map((group) => catalog.gdmtInfo(group)?.max_pts ?? 0),
    );
    const drugs = result.medications
      .filter((m) => applicable.includes(m.drug_class))
      .sort((a, b) => b.points - a.points);
    // Only the best drug in a slot counts, never the sum: the API reports
    // points on every drug it considered, so a patient on an ARNI, an ACE
    // inhibitor and two ARBs comes back 3 + 1 + 1 + 1 while contributing 3 to
    // the score. Both `multiple_drug_classes_in_raas_slot` and
    // `multiple_medications_same_class` mean "highest counted". `drugs` is
    // sorted by points, so the counted one leads.
    const counted = drugs[0];

    return {
      key,
      label,
      maxPoints,
      points: counted?.points ?? 0,
      drugs,
      state: slotState(drugs, catalog),
    };
  }).filter((slot) => slot != null);
}

/**
 * Red / orange / green, per the tracker spec: red when nothing in the slot
 * scores, green when the counted drug is at its target dose, orange in between.
 *
 * The light tracks the *dose*, not the points, because the two disagree in both
 * directions. A presence-scored MRA earns its full 2 points at half the target
 * dose — the spec's own worked example — and still wants up-titrating; an ACE
 * inhibitor at its full target dose earns 2 against the RAAS slot's ARNI-led
 * ceiling of 3 and is not short of anything titratable. Only where the API
 * sends no dose to compare does the drug's own class ceiling stand in.
 *
 * Takes the whole slot and picks the counted drug itself: naming
 * `GDMTMedicationResult | undefined` in the signature would trip
 * `no-redundant-type-constituents` on environments whose API spec predates GDMT
 * (CI lints against the production spec), the same reason `flagHint` derives its
 * parameter from the contract field.
 */
function slotState(
  drugs: GDMTMedicationResult[],
  catalog: SupportedMedications,
): GdmtSlotState {
  const counted = drugs[0];
  // Zero points reads the same whether the class is absent or present but
  // unscoreable: either way the pillar is not contributing.
  if (counted == null || counted.points <= 0) return "missing";

  const dose = counted.daily_dose;
  const target = counted.target_daily_dose;
  if (dose != null && target != null && target > 0) {
    return dose >= target ? "at-target" : "partial";
  }

  const classCeiling = catalog.gdmtInfo(counted.drug_class)?.max_pts ?? 0;
  return counted.points >= classCeiling ? "at-target" : "partial";
}

/** Tailwind classes per state; keyed so a new state fails the build. */
export const slotColor: Record<
  GdmtSlotState,
  { text: string; bg: string; border: string }
> = {
  "at-target": {
    text: "text-green-600",
    bg: "bg-green-600",
    border: "border-green-600/40",
  },
  partial: {
    text: "text-amber-500",
    bg: "bg-amber-500",
    border: "border-amber-500/40",
  },
  missing: {
    text: "text-red-600",
    bg: "bg-red-600",
    border: "border-red-600/40",
  },
};

// Reading order is the grid order: beta / MRA on top, RAAS / SGLT2i below.
const SLOTS: {
  key: GdmtSlotKey;
  label: MessageDescriptor;
  classes: (catalog: SupportedMedications) => string[];
}[] = [
  {
    key: "beta",
    label: msg`Beta-blocker`,
    classes: () => [MedicationGroup.Betablockers],
  },
  { key: "mra", label: msg`MRA`, classes: () => [MedicationGroup.MRAs] },
  {
    // ACE inhibitors, ARBs and ARNI compete for one pillar; the catalog marks
    // them with a shared slot rather than naming them, so read it from there.
    key: "raas",
    label: msg`RAAS inhibitor / ARNI`,
    classes: (catalog) =>
      catalog.gdmtGroups.filter(
        (group) => catalog.gdmtInfo(group)?.raas_slot != null,
      ),
  },
  {
    key: "sglt2",
    label: msg`SGLT2 inhibitor`,
    classes: () => [MedicationGroup["SGLT2 inhibitors"]],
  },
];
